import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import { shouldDisableAddToCart } from "@utils/disableAddToCart";
import {
    DeliveryTypes,
    ProductVisibilityTypes,
    ScrollOffsets,
    StorePickerModalStates,
} from "@utils/enums";
import { debounce } from "@utils/general";
import { isDefined } from "@utils/helpers";
import { ProductConfiguration } from "@utils/productConfiguration/productConfiguration.types";

import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";
import { ProductImpressionModel } from "@services/tracking";

import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import { APIStatus } from "@redux/modules/api/data/cart/cart.module";
import { TPreferredStoreState } from "@redux/modules/api/data/preferredStore/preferredStore.types";
import {
    broadcastMessage,
    clearMessages as clearVisibleMessages,
} from "@redux/modules/value/messages/messages.utils";
import { TProductDetailState } from "@redux/modules/value/productDetail/productDetail.types";
import { translateWithoutContext } from "@redux/modules/value/translations/translations.helpers";
import { useGranite } from "@redux/modules/value/translations/translations.provider";
import { TApiAction } from "@redux/store.types";

import { isAdditionalService } from "@components/Product/ECommerce/eCommerce.helpers";
import { StorePickerModal } from "@components/Store/StorePicker/storePickerModal.container";
import { StorePickerFeature } from "@components/Store/StorePicker/types/storePickerFeature.enum";
import { StorePickerType } from "@components/Store/StorePicker/types/storePickerType.enum";

import Compare from "./compare/compare.component";
import Delivery from "./delivery/delivery.component";
import OrderButton from "./delivery/orderButton/orderButton.container";
import "./eCommerce.scss";
import { ECommerceModel, Tooltips } from "./eCommerce.types";
import Pricing from "./pricing/pricing.component";
import SoldOutStamp from "./soldOutStamp/soldOutStamp.component";
import Variants from "./variants/variants.component";

type EcommerceComponentProps = {
    productDetail: TProductDetailState;
    ecommerceModel: ECommerceModel;
    trackingProduct: ProductImpressionModel;
    preferredStore?: TPreferredStoreState | null;
    onSetProductComparisonSkuList: (skuList: string[]) => void;
    productComparisonSkuList: string[];
    onSubmit: (store: string | null) => void;
    onQuantityChange: (amount: number, min?: number, max?: number) => void;
    onProductDetailChange: (data: Partial<TProductDetailState>) => void;
    onEcommerceBlockVisibleChange: (visible: boolean) => void;
    onConfigurationChange: (config: Record<string, unknown>) => void;
    addToCart: (
        deliveryType: DeliveryTypes,
        quantity: number,
        shopCode: string,
        sku: string,
        ecommerceModel: ECommerceModel,
    ) => Promise<TApiAction>;
    isEcommerceReady: boolean;
    configuration: Record<string, unknown>;
    cart?: CartModel | null;
    tooltips: Tooltips;
    clearMessages: any;
    infoLink: string;
    isSoldOut: boolean;
    deliveryTypesDisabled: boolean;
    isClient: boolean;
    productConfiguration?: ProductConfiguration;
};

const EcommerceComponent = ({
    onEcommerceBlockVisibleChange,
    onSubmit,
    cart,
    clearMessages,
    ecommerceModel,
    productDetail,
    trackingProduct,
    preferredStore,
    addToCart,
    isEcommerceReady,
    onQuantityChange,
    onProductDetailChange,
    tooltips,
    infoLink,
    isSoldOut,
    deliveryTypesDisabled,
    productComparisonSkuList,
    onSetProductComparisonSkuList,
    isClient,
    onConfigurationChange,
    configuration,
    productConfiguration,
}: EcommerceComponentProps) => {
    const { t } = useGranite();

    const deliveryRef = useRef<HTMLDivElement>(null);

    const [storePickerModalState, setStorePickerModalState] =
        useState<StorePickerModalStates>(StorePickerModalStates.Closed);

    const handleScrollEvent = () => {
        if (!isDefined(deliveryRef.current)) return;

        const boundingBox = deliveryRef.current.getBoundingClientRect();

        const location =
            boundingBox.top +
            document.documentElement.scrollTop +
            boundingBox.height * 0.5;

        onEcommerceBlockVisibleChange(location > window.scrollY);
    };

    const debouncedHandleScrollEvent = debounce(handleScrollEvent, 10);

    const handleSubmit = () => {
        if (isSoldOut) return;

        clearMessages();
        clearVisibleMessages();

        const isParentInCart = (
            ecommerceModel.product.accessoryParents || []
        ).some((sku: string) => cart?.items.some(i => i.sku === sku));

        if (
            !ecommerceModel.product.saleableSeparately &&
            (!isParentInCart || !cart)
        ) {
            broadcastMessage({
                id: "pdp-error",
                text: translateWithoutContext(
                    "pdp.error.notSaleableSeparately",
                ),
                status: "danger",
                dismissible: true,
                postFix: {
                    onClick: () => {
                        // is een dichtgeklapt info block op mobile
                        const el = window.document.getElementById(
                            "related-products-slider",
                        );

                        const mobileElement = el?.querySelector(
                            ".info-block__heading-button",
                        );

                        if (
                            mobileElement &&
                            mobileElement instanceof HTMLElement &&
                            !el?.classList.contains("info-block--active")
                        ) {
                            mobileElement.click();
                        }
                        scrollTo({
                            top: Math.max(
                                (el?.getBoundingClientRect().top ?? 0) -
                                    ScrollOffsets.DEFAULT,
                                0,
                            ),
                            behavior: "smooth",
                        });
                    },
                    text: translateWithoutContext(
                        "pdp.error.notSaleableSeparately.goTo",
                    ),
                },
                activeScroll: true,
            });
            return;
        }

        if (productDetail.deliveryType === DeliveryTypes.ClickAndCollect) {
            if (
                preferredStore &&
                !shouldDisableAddToCart(preferredStore.productAvailability)
            ) {
                onSubmit(preferredStore.code);
            } else {
                setStorePickerModalState(
                    () => StorePickerModalStates.AddToCart,
                );
            }
        } else {
            // Is dit effectief mogelijk? Home_delivery met een shop?
            onSubmit(productDetail.shop ? productDetail.shop.code : null);
        }
    };

    const product = ecommerceModel.product;
    const additionalService = isAdditionalService(ecommerceModel);

    useEffect(() => {
        if (!isDefined(deliveryRef.current)) return;

        window.addEventListener("scroll", debouncedHandleScrollEvent);
        return () => {
            window.removeEventListener("scroll", debouncedHandleScrollEvent);
        };
    });

    const storePickerType =
        ecommerceModel.visibilityType ===
            ProductVisibilityTypes.OnlyAvailableInStore ||
        ecommerceModel.visibilityType === ProductVisibilityTypes.Delivery
            ? StorePickerType.INFO
            : StorePickerType.SELECT;

    return (
        <React.Fragment>
            <div
                className={classnames("e-commerce__quick-actions p-2", {
                    "e-commerce__quick-actions--hidden":
                        !productDetail.stickyOrderButton,
                    "e-commerce__quick-actions--has-comparison":
                        !!productComparisonSkuList.length,
                })}
            >
                <OrderButton
                    isEcommerceReady={isEcommerceReady}
                    hasMontageParents={product.montageParents.length > 0}
                    isSoldOut={isSoldOut}
                    productDetail={productDetail}
                    size="lg"
                    onSubmit={handleSubmit}
                />
            </div>
            <div className="e-commerce p-3 mb-4">
                {isSoldOut && <SoldOutStamp />}
                <div>
                    <Pricing
                        visibilityType={ecommerceModel.visibilityType}
                        discountPercentage={ecommerceModel.discountPercentage}
                        cashbackAmount={ecommerceModel.cashbackAmount}
                        priceAfterCashbackIsApplied={
                            ecommerceModel.priceAfterCashbackIsApplied
                        }
                        cashbackUnit={ecommerceModel.cashbackUnit}
                        currentPrice={ecommerceModel.currentPrice}
                        strikethroughPrice={ecommerceModel.strikethroughPrice}
                        strikethroughConversionPrice={
                            ecommerceModel.strikethroughConversionPrice
                        }
                        currentConversionPrice={
                            ecommerceModel.currentConversionPrice
                        }
                        conversionUnit={ecommerceModel.conversionUnit}
                        product={product}
                        isEveryDayLowPrice={!!ecommerceModel.everyDayLowPrice}
                        eDLPImage={ecommerceModel.edlpimage}
                        webOnly={ecommerceModel.webOnly}
                    />

                    <Variants
                        variantGroups={
                            ecommerceModel?.product?.productVariants || []
                        }
                        sku={ecommerceModel.sku}
                    />

                    <div ref={deliveryRef}>
                        <Delivery
                            visibilityType={ecommerceModel.visibilityType}
                            minimumOrderQuantity={
                                ecommerceModel.minimumOrderQuantity
                            }
                            roundingValue={ecommerceModel.roundingValue}
                            deliveryOptions={ecommerceModel.deliveryOptions}
                            preferredStore={preferredStore}
                            product={product}
                            productDetail={productDetail}
                            trackingProduct={trackingProduct}
                            onQuantityChange={onQuantityChange}
                            onProductDetailChange={onProductDetailChange}
                            contextAwareXfPath={
                                ecommerceModel.contextAwareXfPath
                            }
                            tooltips={tooltips}
                            infoLink={infoLink}
                            onSubmit={handleSubmit}
                            onOpenStorePicker={() => {
                                !isSoldOut &&
                                    setStorePickerModalState(
                                        () => StorePickerModalStates.Open,
                                    );
                            }}
                            configuration={configuration}
                            onConfigurationChange={onConfigurationChange}
                            isEcommerceReady={isEcommerceReady}
                            isSoldOut={isSoldOut}
                            deliveryTypesDisabled={deliveryTypesDisabled}
                            productConfiguration={productConfiguration}
                            webOnly={ecommerceModel.webOnly}
                            additionalService={additionalService}
                            isClient={isClient}
                        />
                    </div>

                    {!additionalService && (
                        <div className="e-commerce__actions mt-2">
                            {FeatureToggles.isEnabled(
                                FeatureToggleKeys.PRODUCT_COMPARISON,
                            ) &&
                                !product.boxArticle &&
                                isClient && (
                                    <Compare
                                        sku={product.sku}
                                        productComparisonSkuList={
                                            productComparisonSkuList
                                        }
                                        onSetProductComparisonSkuList={
                                            onSetProductComparisonSkuList
                                        }
                                    />
                                )}
                        </div>
                    )}
                </div>
            </div>
            {isEcommerceReady && storePickerType === StorePickerType.INFO && (
                <StorePickerModal
                    isOpen={
                        storePickerModalState !== StorePickerModalStates.Closed
                    }
                    type={StorePickerType.INFO}
                    displayStockLevel={!product?.boxArticle}
                    infoLink={infoLink}
                    storeCode={preferredStore?.code ?? null}
                    fixedFeatures={
                        productDetail.deliveryType ==
                        DeliveryTypes.ClickAndCollect
                            ? [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]
                            : []
                    }
                    onClose={() =>
                        setStorePickerModalState(StorePickerModalStates.Closed)
                    }
                />
            )}
            {isEcommerceReady && storePickerType === StorePickerType.SELECT && (
                <StorePickerModal
                    isOpen={
                        storePickerModalState !== StorePickerModalStates.Closed
                    }
                    type={StorePickerType.SELECT}
                    displayStockLevel={!product?.boxArticle}
                    infoLink={infoLink}
                    storeCode={preferredStore?.code ?? null}
                    fixedFeatures={
                        productDetail.deliveryType ==
                        DeliveryTypes.ClickAndCollect
                            ? [StorePickerFeature.BUY_ONLINE_PICKUP_IN_STORE]
                            : []
                    }
                    onClose={() =>
                        setStorePickerModalState(StorePickerModalStates.Closed)
                    }
                    submitButtonText={
                        storePickerModalState ===
                        StorePickerModalStates.AddToCart
                            ? t(
                                  "general.storepicker.button.confirmStoreAndOrder",
                              )
                            : ""
                    }
                    onSubmit={(shop, onClose) => {
                        if (
                            storePickerModalState !==
                            StorePickerModalStates.AddToCart
                        ) {
                            onClose && onClose();
                            return;
                        }

                        addToCart(
                            productDetail.deliveryType,
                            productDetail.quantity,
                            shop.code,
                            productDetail.sku,
                            ecommerceModel,
                        ).then(action => {
                            if (action.type === APIStatus.SUCCESS) {
                                onClose && onClose();
                                window.location.hash = "add-to-cart";
                            } else if (action.type === APIStatus.FAILURE) {
                                broadcastMessage({
                                    id: "pdp-error",
                                    text: action.payload.message,
                                    status: "danger",
                                    dismissible: true,
                                });
                            }
                        });
                    }}
                />
            )}
        </React.Fragment>
    );
};

export { EcommerceComponent };
